import React, { useState } from 'react';
import { CreateFlowModal } from './CreateFlowModal';
import { ModalContent } from './shared/FormComponents';
import { CreateFlowBackButton } from './shared/CreateFlowBackButton';
import { ManualCreateForm } from './shared/ManualCreateForm';
import { CreateFlowStepIndicator } from './shared/CreateFlowStepIndicator';
import { AttachToCourseModal } from './AttachToCourseModal';
import axios from 'axios';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { GoalType, ISet } from 'models/set';
import { getContentTypeDisplayName } from './shared/contentTypeUtils';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { getEditSetUrlForReactRouter } from 'utils/pathUtils';
import { useHistory } from 'react-router-dom';

interface IProps {
  goalType: GoalType;
  onClose: () => void;
  onBack: () => void;
  onSetCreated?: () => void;
}

type ContentCreationStep = 'manual-create' | 'attach-to-course';

export const ManualCreateContentModal = (props: IProps) => {
  const { goalType, onClose, onBack, onSetCreated } = props;
  const [currentStep, setCurrentStep] = useState<ContentCreationStep>('manual-create');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [createdSet, setCreatedSet] = useState<ISet | null>(null);

  const user = useCurrentUser();
  const history = useHistory();

  const handleCreateContent = async () => {
    if (!title.trim() || !user?.primaryPartner.id) return;

    setIsCreating(true);
    try {
      const data: any = {
        learn_version: 4,
        goal_type: goalType,
        name: title,
        partner_id: user.primaryPartner.id,
      };

      if (description.trim()) {
        data.description = description;
      }

      const response = await axios.post('/api/v3/sets', data);
      const set = normalizeJSONAPIResponse(response.data);
      setCreatedSet(set);
      setCurrentStep('attach-to-course');
      onSetCreated?.();
    } catch (error) {
      console.error('Error creating content:', error);
      setIsCreating(false);
    }
  };

  const handleBackToManualCreate = () => {
    setCurrentStep('manual-create');
  };

  const handleDone = () => {
    if (!createdSet) {
      throw new Error('Created set is null when trying to navigate to editor');
    }
    const editPath = getEditSetUrlForReactRouter(createdSet.learnVersion, createdSet.id);
    history.push(editPath);
  };

  if (currentStep === 'attach-to-course') {
    if (!createdSet) {
      throw new Error('Created set is null when trying to attach to course');
    }
    return (
      <AttachToCourseModal
        onClose={onClose}
        onBack={handleBackToManualCreate}
        onDone={handleDone}
        contentItem={createdSet}
      />
    );
  }

  const contentTypeDisplayName = getContentTypeDisplayName(goalType);

  return (
    <CreateFlowModal title={`Create ${contentTypeDisplayName} (Manual Create)`} onClose={onClose}>
      <ModalContent>
        <CreateFlowBackButton onClick={onBack} />
        <CreateFlowStepIndicator activeStep={2} />

        <ManualCreateForm
          title={title}
          description={description}
          onTitleChange={setTitle}
          onDescriptionChange={setDescription}
          onSubmit={handleCreateContent}
          isCreating={isCreating}
          submitButtonText={`Create ${contentTypeDisplayName}`}
        />
      </ModalContent>
    </CreateFlowModal>
  );
};
